import React, { useState, useRef, useEffect } from "react";
import { Send, Bot, User, Loader2, Stethoscope, Activity, Settings, Plus, Sparkles, History, X, Menu } from "lucide-react";
import { useChat } from "../hooks/useChat";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../styles/Chat.css";
import { apiService } from "../services/api";
// import { useAuth } from "../hooks/useChat";
// import AuthButton from "../components/oAuth_button";
// import LogoutButton from "../components/LogoutButton";
// import { useToast } from "../hooks/Toast";

type ApiOption = "openai" | "gemini" | "grok" | "llama" | "phi" | "gemma-12b" | "gemma-4b" | "gpt-4o" | "gpt-5";

interface Message {
  id: string;
  content: string;
  sender: "user" | "bot";
  timestamp: Date;
  contexts?: string[]
}

interface HeaderProps {
  selectedApi: ApiOption;
  setSelectedApi: React.Dispatch<React.SetStateAction<ApiOption>>;
}

const Header: React.FC<HeaderProps> = ({ selectedApi, setSelectedApi }) => (
  <header className="chat-header">
    <div className="header-container">
      <div className="header-left">
        <div className="logo-container">
          <Stethoscope className="logo-icon" />
          <Activity className="pulse-icon" />
        </div>
        <div className="header-text">
          <p className="app-subtitle">Your intelligent health companion</p>
        </div>
      </div>
      <div className="header-right">
        <select
          id="api-select"
          value={selectedApi}
          onChange={(e) => setSelectedApi(e.target.value as ApiOption)}
          className="api-selector"
        >
          <option value="openai">🚀 Mercury</option>
          <option value="gemini">✨ Venus</option>
          <option value="grok">🔥 Mars</option>
          <option value="llama">🪐 Jupiter</option>
          <option value="phi">💫 Saturn</option>
          <option value="gemma-12b">🌊 Neptune</option>
          <option value="gemma-4b">❄️ Uranus</option>
          <option value="gpt-4o">🌍 Earth</option>
          <option value="gpt-5">🧊 Pluto</option>
        </select>
      </div>
    </div>
  </header>
  
);

const UserDataForm = () => {
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [isDataSaved, setIsDataSaved] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const storedData = localStorage.getItem("data");
      if (storedData) {
        const parsed = JSON.parse(storedData);
        if (parsed.gender && parsed.age) {
          setIsDataSaved(true);
        }
      }
    }, 500);
  
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    const existing = localStorage.getItem("data");
    if (existing) {
      const { gender, age } = JSON.parse(existing);
      setGender(gender);
      setAge(age);
      if (gender && age) {
        setIsDataSaved(true);
      }
    }
  }, []);

  if (isDataSaved) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">        
        <div className="space-y-6">
          <div className="genderItem">
            <label className="block text-sm font-medium text-gray-700 mb-3 labelItem">
              Gender
            </label>
            <div className="space-y-2 labelItems">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="male"
                  checked={gender === 'male'}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-4 h-4 text-blue-600 cursor-pointer"
                />
                <span className="text-gray-700">Male</span>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="female"
                  checked={gender === 'female'}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-4 h-4 text-blue-600 cursor-pointer"
                />
                <span className="text-gray-700">Female</span>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="other"
                  checked={gender === 'other'}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-4 h-4 text-blue-600 cursor-pointer"
                />
                <span className="text-gray-700">Other</span>
              </label>
            </div>
          </div>
          <div className="ageItem">
            <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-2 label">
              Age
            </label>
            <input
              type="number"
              id="age"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              min="1"
              max="150"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ageInput"
              placeholder="Enter age"
            />
          </div>
        </div>            
        </div>
      </div>
  )
};

const MessageBubble = ({ message }: { message: Message }) => {
  const isUser = message.sender === "user";
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, [])
  return (
    <div className={`message-container ${isUser ? "user-message" : "bot-message"} ${isVisible ? "visible" : ""}`}>
      <div className="message-avatar">
        <div className={`avatar-wrapper ${isUser ? "user-avatar" : "bot-avatar"}`}>
          {isUser ? <User className="avatar-icon" /> : <Bot className="avatar-icon" />}
        </div>
      </div>
      <div className="message-content">
        <div className={`message-bubble ${isUser ? "user-bubble" : "bot-bubble"}`}>
        {isUser && message.contexts && message.contexts.length > 0 && (
            <div className="message-contexts">
              <div className="contexts-header">
                <Settings size={12} />
                <span>Context Applied</span>
              </div>
              <div className="contexts-list">
                {message.contexts.map((ctx, index) => (
                  <span key={index} className="context-tag">
                    {ctx}
                  </span>
                ))}
              </div>
            </div>
          )}
          {isUser ? (
            <ReactMarkdown
            children={message.content}
            remarkPlugins={[remarkGfm]}
            components={{
              p: (props) => <p className="message-text" {...props} />,
              strong: (props) => <strong className="markdown-strong" {...props} />,
              ul: (props) => <ul className="markdown-list" {...props} />,
              ol: (props) => <ol className="markdown-ordered-list" {...props} />,
              li: (props) => <li className="markdown-list-item" {...props} />,
              code: (props) => <code className="markdown-code" {...props} />,
              pre: (props) => <pre className="markdown-pre" {...props} />,
            }}
          />
          ) : (
            <ReactMarkdown
              children={message.content}
              remarkPlugins={[remarkGfm]}
              components={{
                p: (props) => <p className="message-text" {...props} />,
                strong: (props) => <strong className="markdown-strong" {...props} />,
                ul: (props) => <ul className="markdown-list" {...props} />,
                ol: (props) => <ol className="markdown-ordered-list" {...props} />,
                li: (props) => <li className="markdown-list-item" {...props} />,
                code: (props) => <code className="markdown-code" {...props} />,
                pre: (props) => <pre className="markdown-pre" {...props} />,
              }}
            />
          )}
        </div>
        
        {/* Timestamp: Shows when the message was sent in 12-hour format */}
        <div className="message-time">
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
};



const TypingIndicator = () => (
  <div className="message-container bot-message typing-indicator">
    <div className="message-avatar">
      <div className="avatar-wrapper bot-avatar typing-avatar">
        <Bot className="avatar-icon" />
        <div className="typing-pulse"></div>
      </div>
    </div>
    <div className="message-content">
      <div className="message-bubble bot-bubble typing-bubble">
        <div className="typing-animation">
        <div className="typing-indicator">
          <div className="typing-dot" style={{ animationDelay: '0ms' }}></div>
          <div className="typing-dot" style={{ animationDelay: '150ms' }}></div>
          <div className="typing-dot" style={{ animationDelay: '300ms' }}></div>
        </div>
          <span className="typing-text">AI is thinking...</span>
        </div>
      </div>
    </div>
  </div>
);

const LeftSidebar = ({
  isOpen,
  onClose,
  history,
  selectedHistory,
  onToggleHistory
}: {
  isOpen: boolean;
  onClose: () => void;
  history: string[];
  selectedHistory: string[];
  onToggleHistory: (item: string) => void;
}) => (
  <div className={`sidebar left-sidebar ${isOpen ? 'open' : ''}`}>
    <div className="sidebar-header">
      <div className="sidebar-title">
        <History className="sidebar-icon" />
        <h3>Chat History</h3>
      </div>
      <button onClick={onClose} className="close-btn">
        <X size={18} />
      </button>
    </div>
    <div className="sidebar-content">
      {history.length > 0 ? (
        history.map((item, index) => (
          <label
            key={index}
            className={`history-item ${selectedHistory.includes(item) ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              checked={selectedHistory.includes(item)}
              onChange={() => onToggleHistory(item)}
              className="history-checkbox"
            />
            <span className="history-text">{item}</span>
          </label>
        ))
      ) : (
        <div className="empty-sidebar">
          <History className="empty-icon" />
          <p>No previous conversations</p>
        </div>
      )}
    </div>
  </div>
);

const RightSidebar = ({
  isOpen,
  onClose,
  contexts,
  selectedContexts,
  onAddContext,
  onRemoveContext,
  onToggleContext
}: {
  isOpen: boolean;
  onClose: () => void;
  contexts: string[];
  selectedContexts: string[];
  onAddContext: (context: string) => void;
  onRemoveContext: (context: string) => void;
  onToggleContext: (context: string) => void;
}) => {
  const [contextInput, setContextInput] = useState("");

  const handleAddContext = () => {
    if (contextInput.trim()) {
      onAddContext(contextInput.trim());
      setContextInput("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddContext();
    }
  };

  return (
    <div className={`sidebar right-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-title">
          <Settings className="sidebar-icon" />
          <h3>Context</h3>
        </div>
        <button onClick={onClose} className="close-btn">
          <X size={18} />
        </button>
      </div>
      <div className="sidebar-content">
        <div className="context-input-section">
          <input
            type="text"
            placeholder="Medical history notes"
            value={contextInput}
            onChange={(e) => setContextInput(e.target.value)}
            onKeyPress={handleKeyPress}
            className="context-input"
          />
          <button
            onClick={handleAddContext}
            disabled={!contextInput.trim()}
            className={`add-context-btn ${contextInput.trim() ? 'active' : 'disabled'}`}
          >
            <Plus className="plus-icon" />
            Add Context
          </button>
        </div>

        {contexts.length > 0 && (
          <div className="contexts-display">
            <div style={{ marginBottom: '12px', fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>
              Selected: {selectedContexts.length}/5
            </div>
            {contexts.map((ctx) => (
              <label
                key={ctx}
                className={`context-chip ${selectedContexts.includes(ctx) ? 'selected' : ''}`}
                style={{ 
                  cursor: 'pointer',
                  opacity: !selectedContexts.includes(ctx) && selectedContexts.length >= 5 ? 0.6 : 1
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedContexts.includes(ctx)}
                  onChange={() => onToggleContext(ctx)}
                  disabled={!selectedContexts.includes(ctx) && selectedContexts.length >= 5}
                  style={{ 
                    cursor: selectedContexts.includes(ctx) || selectedContexts.length < 5 ? 'pointer' : 'not-allowed',
                    marginRight: '10px'
                  }}
                />
                <span className="context-text">{ctx}</span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onRemoveContext(ctx);
                  }}
                  className="remove-context-btn"
                >
                  ×
                </button>
              </label>
            ))}
          </div>
        )}

        {contexts.length === 0 && (
          <div className="empty-sidebar">
            <Sparkles className="empty-icon" />
            <p>Add personal context to get more relevant health advice</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ChatInput = ({
  onSendMessage,
  disabled,
  hasSelectedHistory,
  prefill = ""
}: {
  onSendMessage: (message: string) => void;
  disabled: boolean;
  hasSelectedHistory: boolean;
  prefill?: string;
}) => {
  const [message, setMessage] = useState(prefill);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = () => {
    if ((message.trim() || hasSelectedHistory) && !disabled) {
      onSendMessage(message.trim());
      setMessage("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Only handle Enter key if this input is focused
    if (e.key === "Enter" && !e.shiftKey && document.activeElement === inputRef.current) {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  useEffect(() => {
    if (prefill) {
      setMessage(prefill);
    }
  }, [prefill]);
  
  

useEffect(() => {
  if (inputRef.current) {
    inputRef.current.style.height = 'auto'; // Reset height
    inputRef.current.style.height = `${inputRef.current.scrollHeight}px`; // Set to scroll height
  }
}, [message]);

  return (
    <div className="chat-input-container">
      <div className="input-wrapper">
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Ask me about symptoms, health tips..."
        disabled={disabled}
        className="message-input textarea-input"
        rows={1}
      />
        <button
          onClick={handleSubmit}
          disabled={disabled || (!message.trim() && !hasSelectedHistory)}
          className={`send-button ${disabled || (!message.trim() && !hasSelectedHistory) ? "disabled" : "active"}`}
        >
          {disabled ? (
            <Loader2 className="button-icon spinning" />
          ) : (
            <>
              <Send className="button-icon" />
              Send
            </>
          )}
        </button>
      </div>
    </div>
  );
};




const Chat: React.FC = () => {
  const [selectedApi, setSelectedApi] = useState<ApiOption>("openai");
  const [contexts, setContexts] = useState<string[]>([]);
  const [selectedContexts, setSelectedContexts] = useState<string[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<string[]>([]);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [hasUserData, setHasUserData] = useState<boolean>(false);
  // const { user, setUser } = useAuth();
  
  hasUserData
  const { history, messages, isLoading, sendMessage, extraQuestions } = useChat(selectedApi);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  

  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId) {
      // setUserId(storedUserId);
    } else {
      const newUserId = `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("userId", newUserId);
    }
  }, []);

  useEffect(() => {
    const storedData = localStorage.getItem("data");
    if (storedData) {
      setHasUserData(true);
    } else {
      setHasUserData(false);
    }
  }, []);
  

  useEffect(() => {
    const fetchContexts = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const res = await apiService.get<{ success: boolean; data: string[]; message?: string }>(
          `/chat/contexts/${userId}`
        );

        if (res.success && res.data) {
          setContexts(res.data);
        }
      } catch (error) {
        console.error("Error fetching contexts:", error);
      }
    };

    fetchContexts();
  }, []);

  const handleAddContext = async (context: string) => {
    if (contexts.includes(context)) {
      alert("This context already exists!")
      return;
    }

    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        alert("User ID not found - Please reload the Page")
        return;
      }

      const response = await apiService.post<{ success: boolean; message?: string }>(
        `/chat/add/${userId}`,
        { context }
      );

      if (response.success) {
        setContexts(prev => [...prev, context]);
        if (selectedContexts.length < 5) {
          setSelectedContexts(prev => [...prev, context]);
        }
      } else {
        alert("Failed to add context")
        // showToast("Failed to add context", "info");
      }
    } catch (error) {
      console.error("Error adding context:", error);
      alert("Error adding context")
      // showToast("Error adding context", "error");
    }
  };

  const handleRemoveContext = async (context: string) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        alert("User ID not found")
        // showToast("User ID not found", "warning");
        return;
      }

      const response = await apiService.post<{ success: boolean; message?: string }>(
        `/chat/remove/${userId}`,
        { context }
      );

      if (response.success) {
        setContexts(prev => prev.filter(c => c !== context));
        setSelectedContexts(prev => prev.filter(c => c !== context));
      } else {
        alert("Failed to remove context")
        // showToast("Failed to remove context", "warning");
      }
    } catch (error) {
      alert("Error removing context")
      console.error("Error removing context:", error);
      // showToast("Error removing context", 'error');
    }
  };

  const handleToggleContext = (context: string) => {
    setSelectedContexts(prev => {
      if (prev.includes(context)) {
        return prev.filter(c => c !== context);
      } else {
        if (prev.length < 5) {
          return [...prev, context];
        }
        return prev;
      }
    });
  };

  const handleToggleHistory = (item: string) => {
    setSelectedHistory(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  const handleSendMessage = (message: string) => {
    sendMessage(message, [...selectedContexts], [...selectedHistory]);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  return (
    <div className="app-container">
      {/* <ToastContainer /> */}
      <Header selectedApi={selectedApi} setSelectedApi={setSelectedApi} />
      {/* <div className="google-bar">
      { user ? (
            <div className="user-data">
              <div>
                <h2 className="user-name">Welcome, {user.name}</h2>
                <p className="user-email">{user.email}</p>
              </div>
              <LogoutButton onLogout={() => setUser(null)} />
            </div>
          ) : (
            <></>
          )}
      </div> */}
      <div className="main-layout">
        <LeftSidebar
          isOpen={leftSidebarOpen}
          onClose={() => setLeftSidebarOpen(false)}
          history={history}
          selectedHistory={selectedHistory}
          onToggleHistory={handleToggleHistory}
        />

        <main className="chat-main">
          {!leftSidebarOpen && (
            <button
              onClick={() => setLeftSidebarOpen(true)}
              className="toggle-btn left-toggle"
            >
              <Menu size={20} />
            </button>
          )}
          {!rightSidebarOpen && (
            <button
              onClick={() => setRightSidebarOpen(true)}
              className="toggle-btn right-toggle"
            >
              <Settings size={20} />
            </button>
          )}

          <div className="messages-container">
            <div className="messages-wrapper">
              {messages.length === 0 && (
                <div className="welcome-message">
                  <div className="welcome-content">
                    <div className="welcome-icon">
                      <Stethoscope />
                    </div>
                    <h2>Welcome to HealthCare Assistant AI</h2>
                    <p>I'm here to help you with health-related questions, symptom analysis, and wellness advice.</p>
                  </div>
                </div>
              )}

              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              {isLoading && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          </div>
          
          <UserDataForm />    
          <ChatInput 
            onSendMessage={handleSendMessage} 
            disabled={isLoading}
            hasSelectedHistory={selectedHistory.length > 0}
            prefill={extraQuestions.length > 0 ? extraQuestions.map((q, index) => `${index + 1}. ${q}`).join("\n\n") + "\n" : ""}
          />

        </main>

        <RightSidebar
          isOpen={rightSidebarOpen}
          onClose={() => setRightSidebarOpen(false)}
          contexts={contexts}
          selectedContexts={selectedContexts}
          onAddContext={handleAddContext}
          onRemoveContext={handleRemoveContext}
          onToggleContext={handleToggleContext}
        />
      </div>
    </div>
  );
};

export default Chat;